import { useEffect } from 'react'

import { useBridge } from '../bridge'
import { detectWebView } from './detectWebView'

interface WebViewGuardProps {
  fallbackUrl: string
  children: React.ReactNode
}

/**
 * 앱 밖(일반 브라우저 등) 진입 시 깨진 화면 대신 랜딩으로 보내는 **UX 폴백**.
 * 개발 환경에서는 체크를 스킵해 로컬 브라우저 디버깅을 허용한다.
 *
 * ⚠️ 접근 제어가 아니다. UA 도 브릿지 객체도 브라우저 콘솔에서 위조 가능하다.
 *    실제 보호는 서버의 API 키·토큰이 담당한다.
 *
 * 판정 근거는 **브릿지 실재 여부 하나뿐**이다.
 *  - isTimeout(5초 내 미주입) → 차단
 *  - 그 외(ready/connecting)  → 통과. 대기 중 로딩 UI 는 children 담당.
 *
 * ⚠️ **UA 를 차단 근거로 쓰지 않는다.** UA 패턴은 오탐이 잦은데(iPadOS 13+ 데스크톱 UA,
 *    Flutter 가 UA 뒤에 앱 토큰을 붙여 `Safari/` 가 섞이는 경우 등) 오탐 한 번이면 정상 사용자가
 *    앱을 통째로 못 보는 전면 장애가 된다. 오탐 비용이 오검출 비용보다 압도적으로 크므로
 *    위조 가능한 신호로 앞당겨 차단하지 않고 브릿지 판정을 기다린다.
 *    UA 는 차단 사유 진단용으로만 기록한다.
 */
export function WebViewGuard({ fallbackUrl, children }: WebViewGuardProps) {
  const { isTimeout } = useBridge()
  const isDev = import.meta.env.DEV
  const shouldRedirect = !isDev && isTimeout

  useEffect(() => {
    if (!shouldRedirect) return
    // UA 는 판정에 쓰지 않지만, 차단 사례 분석 시 "정상 웹뷰인데 브릿지만 늦었나"를 가르는 단서가 된다
    console.error('[WebViewGuard] blocked', { reason: 'bridge-timeout', uaLooksLikeWebView: detectWebView() })
    window.location.href = fallbackUrl
  }, [shouldRedirect, fallbackUrl])

  if (shouldRedirect) return null
  return <>{children}</>
}
